import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import '../components/landing.css';
import './getting-started.css';

const APP_STORE_URL = 'https://apps.apple.com/sa/app/paddiq/id6785660120';
const PAGE_TITLE = 'Import and assign a MyChron session on iPhone | Paddiq';

export function ImportMyChronIPhonePage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">iPhone sessions guide</p>
            <h1>Import a MyChron session on iPhone.</h1>
            <p className="guide-hero__lead">
              Download sessions from a MyChron over Wi-Fi or choose a MyChron JSON export, then
              review the driver, vehicle, circuit, and recorded time before uploading.
            </p>
            <div className="guide-hero__actions">
              <a className="landing-btn landing-btn--primary landing-btn--lg" href={APP_STORE_URL}>
                Download on the App Store
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/upload-mychron-session"
              >
                Upload on the web
              </a>
            </div>
            <p className="guide-hero__meta">iPhone guide · Reviewed October 15, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/import-mychron-iphone" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#logger">Set up the connection</a>
                </li>
                <li>
                  <a href="#download">Download from a logger</a>
                </li>
                <li>
                  <a href="#file">Import a JSON file</a>
                </li>
                <li>
                  <a href="#assign">Review and upload</a>
                </li>
                <li>
                  <a href="#troubleshooting">Recover a saved session</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you start</strong>
                <span>
                  Sign in and power on the MyChron. The iPhone joins the logger Wi-Fi for the
                  download, then needs internet access for the cloud upload.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="logger">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Direct logger download</p>
                  <h2>Choose a saved MyChron or add another.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Loggers</strong>.
                    </li>
                    <li>
                      Tap a saved MyChron to connect, or select <strong>Add logger</strong> and then
                      <strong>MyChron</strong>.
                    </li>
                    <li>
                      Choose <strong>Previous accessories</strong>, or select{' '}
                      <strong>Find Accessories</strong> to authorize another logger with Apple's
                      picker.
                    </li>
                    <li>
                      Follow the connection steps while Paddiq verifies the actual Wi-Fi network and
                      detects the logger address.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    Paddiq never removes a saved Apple accessory during search. Forgetting one is a
                    separate confirmed action.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="download">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Session selection</p>
                  <h2>Choose the sessions to download.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Select <strong>Connect &amp; list sessions</strong>.
                    </li>
                    <li>Review the available session details and select one or more runs.</li>
                    <li>
                      Select <strong>Download &amp; assign</strong>.
                    </li>
                    <li>Wait while Paddiq saves and prepares the logger files.</li>
                  </ol>
                  <p>
                    Paddiq stores the downloaded files on the iPhone before cloud upload, so a lost
                    internet connection does not require another logger download.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="file">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">JSON file import</p>
                  <h2>Start from a file already on the iPhone.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Tap the <strong>+</strong> action in the bottom navigation.
                    </li>
                    <li>
                      Select <strong>Import file</strong>.
                    </li>
                    <li>
                      Choose one MyChron <strong>.json</strong> export.
                    </li>
                    <li>
                      Wait for <strong>Verify circuit</strong> to open.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    The iPhone document picker accepts MyChron JSON exports. To download XRZ and
                    other files directly from your logger, use the Wi-Fi option instead.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="assign">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Assign and upload</p>
                  <h2>Confirm the run before it reaches the cloud.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Review the detected track, driver, vehicle, laps, channels, and recorded time.
                    </li>
                    <li>
                      Correct the preselected <strong>Driver</strong> or <strong>Vehicle</strong> if
                      needed.
                    </li>
                    <li>
                      Review the recommended circuit against the recorded lap, choose another
                      circuit, or select <strong>No circuit</strong> when no match is trustworthy.
                    </li>
                    <li>
                      Select <strong>Confirm &amp; upload</strong> for a confirmed circuit, or{' '}
                      <strong>Upload without circuit</strong>. In a batch, use{' '}
                      <strong>Confirm &amp; continue</strong> or{' '}
                      <strong>Upload &amp; continue</strong> to move through the runs.
                    </li>
                  </ol>
                  <p>
                    The choices shown depend on your account access. A driver uses their own
                    profile, a parent sees linked drivers, and team roles use the roster available
                    to their account.
                  </p>
                  <p>
                    Wait for the upload and save work to finish. Paddiq opens the uploaded session
                    for review after the queue completes.
                  </p>
                  <h3>Resume a download saved on this iPhone.</h3>
                  <ol className="guide-instructions">
                    <li>
                      Return to an internet connection, then open <strong>Loggers</strong> and tap{' '}
                      <strong>Saved</strong> on the iPhone used for the download.
                    </li>
                    <li>
                      Find the run under <strong>Ready to attach</strong>. Prepared files show{' '}
                      <strong>Awaiting assignment</strong>; other downloads show{' '}
                      <strong>Tap to prepare</strong>.
                    </li>
                    <li>
                      Tap the run or <strong>Attach</strong> to open <strong>Verify circuit</strong>
                      , then complete the assignment and upload steps above.
                    </li>
                  </ol>
                  <p>
                    The Saved list is titled Sessions, but it is separate from the{' '}
                    <strong>Sessions</strong> analysis screen in the bottom navigation. A local
                    download is not yet an uploaded session available for analysis on another
                    device.
                  </p>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If the import stops</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>The logger does not appear</h3>
                    <p>
                      Confirm the iPhone joined the selected MyChron Wi-Fi, then retry the saved
                      logger. Paddiq verifies Wi-Fi before checking supported logger addresses.
                    </p>
                  </article>
                  <article>
                    <h3>The cloud is unreachable</h3>
                    <p>
                      Return to cellular data or your usual Wi-Fi. Open <strong>Loggers</strong> →{' '}
                      <strong>Saved</strong> to find the downloaded run under{' '}
                      <strong>Ready to attach</strong>.
                    </p>
                  </article>
                  <article>
                    <h3>The file is not ready to assign</h3>
                    <p>
                      Open <strong>Loggers</strong> → <strong>Saved</strong> and tap the download to
                      prepare it again. A prepared run is marked{' '}
                      <strong>Awaiting assignment</strong>.
                    </p>
                  </article>
                  <article>
                    <h3>Uploads are paused</h3>
                    <p>
                      Follow the account prompt, then return to <strong>Loggers</strong> →{' '}
                      <strong>Saved</strong>. Pending downloads appear under{' '}
                      <strong>Saved on this device</strong> while access is paused. Your downloaded
                      file and assignment choices remain saved.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">At the track</p>
                  <h2>Move the run from logger to review.</h2>
                  <p>
                    Download over logger Wi-Fi, reconnect to the internet, then upload the checked
                    assignments.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={APP_STORE_URL}
                  >
                    Get Paddiq for iPhone
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
